import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { PhoneInput } from '../components/ui/phone-input';
import { StatesSelect } from '../components/ui/states-select';
import { User, Calendar, BookOpen, Settings, Crown, Users, Edit, Lock, Mail, Phone, MapPin } from 'lucide-react';

const MemberDashboard = () => {
  const { user, logout, checkAuthentication } = useAuth();
  const [membershipType, setMembershipType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  
  // Profile editing states
  const [profileData, setProfileData] = useState({
    name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [emailData, setEmailData] = useState({
    new_email: '',
    password: ''
  });

  useEffect(() => {
    if (user) {
      fetchMembershipInfo();
      setProfileData({
        name: user.name || '',
        phone_number: user.phone_number || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || ''
      });
    }
  }, [user]);

  const fetchMembershipInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/membership-types`, {
        credentials: 'include',
      });
      if (response.ok) {
        const membershipTypes = await response.json();
        const userMembership = membershipTypes.find(type => type.id === user?.membership_type_id);
        setMembershipType(userMembership);
      }
    } catch (error) {
      console.error('Error fetching membership info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSession = () => {
    window.open('https://thewisdomnetwork.as.me/', '_blank', 'noopener noreferrer');
  };

  const goToMembershipPage = () => {
    window.location.href = '/membership';
  };

  const showMessage = (type, content) => {
    setMessage({ type, content });
    setTimeout(() => setMessage({ type: '', content: '' }), 5000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const result = await response.json();
        showMessage('success', 'Profile updated successfully!');
        // Refresh user data
        await checkAuthentication();
      } else {
        const errorData = await response.json();
        showMessage('error', errorData.detail || 'Profile update failed');
      }
    } catch (error) {
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      showMessage('error', 'New password must be at least 6 characters');
      return;
    }

    setEditLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        }),
      });

      if (response.ok) {
        showMessage('success', 'Password changed successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const errorData = await response.json();
        showMessage('error', errorData.detail || 'Password change failed');
      }
    } catch (error) {
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-email`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_email: emailData.new_email,
          password: emailData.password
        }),
      });

      if (response.ok) {
        showMessage('success', 'Email address changed successfully!');
        setEmailData({ new_email: '', password: '' });
        // Refresh user data
        await checkAuthentication();
      } else {
        const errorData = await response.json();
        showMessage('error', errorData.detail || 'Email change failed');
      }
    } catch (error) {
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-purple-800 mb-4 font-title">Access Denied</h1>
          <p className="text-lg text-purple-700 font-body">Please log in to access your member dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-purple-800 font-title">Member Dashboard</h1>
              <p className="text-gray-600 font-body">Welcome back, {user.name}!</p>
            </div>
            <div className="flex items-center space-x-4">
              {membershipType && (
                <div className="flex items-center space-x-2 bg-purple-100 px-4 py-2 rounded-lg">
                  <Crown className="h-5 w-5 text-purple-600" />
                  <span className="text-purple-700 font-semibold">{membershipType.name}</span>
                </div>
              )}
              <Button onClick={logout} variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message Display */}
        {message.content && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.content}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveSection('overview')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'overview'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <BookOpen className="h-4 w-4 inline-block mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveSection('profile')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'profile'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <Edit className="h-4 w-4 inline-block mr-2" />
            Edit Profile
          </button>
          <button
            onClick={() => setActiveSection('security')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'security'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <Lock className="h-4 w-4 inline-block mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveSection('membership')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'membership'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <Crown className="h-4 w-4 inline-block mr-2" />
            Membership
          </button>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Welcome Card */}
          <Card className="col-span-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-6 w-6" />
                <span>Welcome to The Wisdom Network</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-purple-100 mb-4">
                Access transformative spiritual guidance from our certified practitioners. 
                Your journey to wisdom and healing starts here.
              </p>
              {membershipType && (
                <div className="bg-white/20 rounded-lg p-3 mb-4">
                  <h4 className="font-semibold mb-2">Your Membership: {membershipType.name}</h4>
                  <p className="text-sm text-purple-100">{membershipType.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-purple-800">
                <Calendar className="h-5 w-5" />
                <span>Book a Session</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Schedule a session with one of our certified practitioners.</p>
              <Button 
                onClick={handleScheduleSession}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Schedule Now
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-purple-800">
                <Users className="h-5 w-5" />
                <span>Browse Practitioners</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Explore our network of certified spiritual practitioners.</p>
              <Button 
                onClick={() => window.location.href = '/practitioners'}
                variant="outline" 
                className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
              >
                View Practitioners
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-purple-800">
                <BookOpen className="h-5 w-5" />
                <span>Resources</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                {membershipType?.permissions?.can_access_resources 
                  ? "Access exclusive member resources and content."
                  : "Upgrade your membership to access premium resources."
                }
              </p>
              {membershipType?.permissions?.can_access_resources ? (
                <Button
                  variant="outline"
                  className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
                  disabled
                >
                  View Resources
                </Button>
              ) : (
                <Button
                  onClick={goToMembershipPage}
                  variant="outline"
                  className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
                >
                  Upgrade Membership
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card className="col-span-full">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-purple-800">
                <Settings className="h-5 w-5" />
                <span>Account Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Profile Details</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div><span className="font-medium">Name:</span> {user.name}</div>
                    <div><span className="font-medium">Email:</span> {user.email}</div>
                    <div><span className="font-medium">Phone:</span> {user.phone_number || 'Not provided'}</div>
                    <div><span className="font-medium">Address:</span> {user.address || 'Not provided'}</div>
                    {user.city && user.state && (
                      <div><span className="font-medium">Location:</span> {user.city}, {user.state} {user.zip_code}</div>
                    )}
                    <div><span className="font-medium">Member Since:</span> {new Date(user.created_date).toLocaleDateString()}</div>
                  </div>
                </div>
                {membershipType && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Membership Benefits</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div>✓ Book sessions with practitioners</div>
                      {membershipType.permissions.can_access_premium_content && <div>✓ Access premium content</div>}
                      {membershipType.permissions.can_access_resources && <div>✓ Exclusive member resources</div>}
                      <div>✓ {membershipType.permissions.max_sessions_per_month === -1 ? 'Unlimited' : membershipType.permissions.max_sessions_per_month} sessions per month</div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Profile Editing Section */}
        {activeSection === 'profile' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-purple-800">
                <Edit className="h-5 w-5" />
                <span>Edit Profile Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      placeholder="Enter your full name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <PhoneInput
                      id="phone"
                      value={profileData.phone_number}
                      onChange={(formatted) => setProfileData({...profileData, phone_number: formatted})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={profileData.address}
                    onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                    placeholder="123 Main Street"
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={profileData.city}
                      onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                      placeholder="City"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <StatesSelect
                      value={profileData.state}
                      onValueChange={(value) => setProfileData({...profileData, state: value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP Code</Label>
                    <Input
                      id="zip"
                      value={profileData.zip_code}
                      onChange={(e) => setProfileData({...profileData, zip_code: e.target.value})}
                      placeholder="12345"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={editLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {editLoading ? 'Updating...' : 'Update Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Security Section */}
        {activeSection === 'security' && (
          <div className="space-y-6">
            {/* Change Password */}
            {user?.auth_method === 'password' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-purple-800">
                    <Lock className="h-5 w-5" />
                    <span>Change Password</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label htmlFor="current_password">Current Password</Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        placeholder="Enter current password"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="new_password">New Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                          placeholder="Enter new password"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm_password">Confirm New Password</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                          placeholder="Confirm new password"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={editLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {editLoading ? 'Changing...' : 'Change Password'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Change Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-purple-800">
                  <Mail className="h-5 w-5" />
                  <span>Change Email Address</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div>
                    <Label htmlFor="current_email">Current Email</Label>
                    <Input
                      id="current_email"
                      type="email"
                      value={user?.email}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_email">New Email Address</Label>
                    <Input
                      id="new_email"
                      type="email"
                      value={emailData.new_email}
                      onChange={(e) => setEmailData({...emailData, new_email: e.target.value})}
                      placeholder="Enter new email address"
                      className="mt-1"
                      required
                    />
                  </div>
                  {user?.auth_method === 'password' && (
                    <div>
                      <Label htmlFor="email_password">Confirm with Password</Label>
                      <Input
                        id="email_password"
                        type="password"
                        value={emailData.password}
                        onChange={(e) => setEmailData({...emailData, password: e.target.value})}
                        placeholder="Enter your password"
                        className="mt-1"
                        required
                      />
                    </div>
                  )}
                  <Button 
                    type="submit" 
                    disabled={editLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {editLoading ? 'Changing...' : 'Change Email'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Membership Section */}
        {activeSection === 'membership' && (
          <div className="space-y-6">
            {/* Current Membership */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Crown className="h-5 w-5" />
                  <span>Current Membership</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-2">
                      {membershipType ? membershipType.name : 'No Active Membership'}
                    </h4>
                    <p className="text-purple-700 text-sm">
                      {membershipType ? membershipType.description : 'You currently do not have an active membership.'}
                    </p>
                    <div className="mt-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user?.status === 'active' ? 'bg-green-100 text-green-800' :
                        user?.status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                        user?.status === 'expired' ? 'bg-red-100 text-red-800' :
                        user?.status === 'suspended' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        Status: {user?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Available Packages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Crown className="h-5 w-5" />
                  <span>Upgrade Your Membership</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* General Membership */}
                  <div className="border rounded-lg p-6 space-y-4">
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900">General Membership</h3>
                      <p className="text-gray-600 text-sm mt-1">Essential spiritual guidance</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-purple-600">$97</div>
                        <div className="text-sm text-gray-500">per month</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">$970</div>
                        <div className="text-sm text-gray-500">per year <span className="text-green-600">(Save $194!)</span></div>
                      </div>
                    </div>

                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Access to general practitioner sessions</li>
                      <li>• Basic spiritual guidance resources</li>
                      <li>• Community forum access</li>
                      <li>• Monthly newsletter</li>
                    </ul>

                    <div className="space-y-2">
                      <Button 
                        onClick={goToMembershipPage}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        disabled={user?.membership_type_id === 'general'}
                      >
                        {user?.membership_type_id === 'general' ? 'Current Plan' : 'Get Monthly - $97'}
                      </Button>
                      <Button 
                        onClick={goToMembershipPage}
                        variant="outline"
                        className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
                        disabled={user?.membership_type_id === 'general'}
                      >
                        {user?.membership_type_id === 'general' ? 'Current Plan' : 'Get Yearly - $970'}
                      </Button>
                    </div>
                  </div>

                  {/* Power Portal Membership */}
                  <div className="border-2 border-purple-600 rounded-lg p-6 space-y-4 relative">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-xs font-medium">
                        MOST POPULAR
                      </span>
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900">Power Portal</h3>
                      <p className="text-gray-600 text-sm mt-1">Premium spiritual transformation</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-purple-600">$197</div>
                        <div className="text-sm text-gray-500">per month</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">$1,970</div>
                        <div className="text-sm text-gray-500">per year <span className="text-green-600">(Save $394!)</span></div>
                      </div>
                    </div>

                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Everything in General membership</li>
                      <li>• Priority booking with top practitioners</li>
                      <li>• Exclusive premium content library</li>
                      <li>• 1-on-1 monthly coaching sessions</li>
                      <li>• Access to advanced workshops</li>
                      <li>• Personal transformation tracking</li>
                    </ul>

                    <div className="space-y-2">
                      <Button 
                        onClick={goToMembershipPage}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        disabled={user?.membership_type_id === 'power-portal'}
                      >
                        {user?.membership_type_id === 'power-portal' ? 'Current Plan' : 'Get Monthly - $197'}
                      </Button>
                      <Button 
                        onClick={goToMembershipPage}
                        variant="outline"
                        className="w-full border-purple-600 text-purple-600 hover:bg-purple-50"
                        disabled={user?.membership_type_id === 'power-portal'}
                      >
                        {user?.membership_type_id === 'power-portal' ? 'Current Plan' : 'Get Yearly - $1,970'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDashboard;